/**
 * bridge/sheet_controller.js — "sheet" bridge component (Stimulus + Strada).
 *
 * Hands a URL to the platform's own bottom-sheet presenter, so a create/edit
 * form arrives as a real UISheetPresentationController on iOS or a real
 * BottomSheetDialogFragment on Android instead of a CSS drawer painted inside
 * the page. Stimulus identifier "bridge--sheet" (glob-derived from this subdir);
 * Strada component name "sheet" MUST match the iOS class var name
 * (native/ios/Chippie/Bridge/SheetComponent.swift).
 *
 * THE FACADE KEY IS "presentSheet", NOT "sheet" — the one component where the
 * two names differ. "sheet" names a thing; the facade call does something, and
 * ChippieBridge.sheet(url) reads as a getter for a sheet rather than a request
 * to present one. The Strada/Swift side keeps the noun because that is the class
 * it names. Nothing enforces the two being equal: register() takes the facade
 * key, `static component` takes the channel name.
 *
 * Not to be confused with static/js/controllers/esm/sheet_controller.js — that
 * one is the WEB drawer (the .is-sheet presentation over Bootstrap's modal).
 * This one is the seam that lets a native host take the job instead. The web
 * controller asks first and only paints when nothing native answers.
 *
 * WHY A URL AND NOT MARKUP
 * A native sheet hosts its own web view, and a web view needs an address. It
 * cannot be handed a detached DOM subtree. That is the entire reason
 * application/web/views/native.py exposes standalone /quotes/new,
 * /quotes/<id>/edit and friends: those pages are what a sheet loads. The URL a
 * caller passes here must be one of them, and the path-configuration rules
 * served by that same module are what tell the shells these paths are sheets.
 *
 * API — window.ChippieBridge.presentSheet(url) -> Boolean (did a native host
 * take it):
 *   url  a same-origin app path, e.g. "/quotes/17/edit".
 *
 * The RETURN VALUE is load-bearing and is the whole contract. Truthy means a
 * native sheet is being presented and the caller must NOT also paint the web
 * drawer; falsy means nothing took the job and the web drawer is the answer.
 * Every impl below therefore reports honestly rather than optimistically — a
 * false positive leaves the user staring at a modal that never opens.
 *
 * Three-way transport (facade SSOT, chippie-bridge.js owns platform detection):
 *   - ios:     this.send("present", { url }) -> a UISheetPresentationController
 *              wrapping a second WKWebView built from Hotwire.config.makeWebView().
 *   - android: window.ChippieNative.presentSheet(url) -> SheetFragment, a
 *              BottomSheetDialogFragment over the same shared WebView setup.
 *   - pwa:     false. A browser has no native host, so the web drawer runs.
 *              This is the correct answer, not a missing implementation.
 *
 * NOT server-drivable: a sheet that appears with no user gesture behind it is
 * indistinguishable from a bug, so this is absent from REMOTE_ALLOWED for the
 * same reason bridge--print is.
 *
 * Template wiring: mounted once app-wide on components/bridge_host.html. It
 * needs no per-element instance because the caller is the web sheet controller
 * on <body>, not a button.
 *
 * Outside a native shell BridgeComponent never connect()s, so the Android/PWA
 * impls are wired standalone at module load and connect() upgrades iOS.
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// Only same-origin app paths may be presented. A sheet is chrome-less by
// design — no address bar, no origin indicator — so handing it an off-site URL
// would render a third party's page inside what the user reads as the app.
// Anything that is not a rooted path is refused, and the caller falls back to
// the web drawer, which at least stays inside the page it came from.
function appPath(url) {
    const value = String(url || "");
    // "//evil.example" is a protocol-relative absolute URL, not an app path.
    return /^\/(?!\/)/.test(value) ? value : null;
}

function androidPresent(url) {
    const path = appPath(url);
    if (!path) return false;
    if (!window.ChippieNative || !window.ChippieNative.presentSheet) return false;
    try {
        window.ChippieNative.presentSheet(path);
        return true;
    } catch (_e) {
        return false;
    }
}

// A browser has no native sheet host. Reporting false is what makes the web
// drawer run, so this is the implementation, not a stub.
function pwaPresent() {
    return false;
}

function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("presentSheet", {
        // NO ios KEY, deliberately. The facade resolves
        // `impls[platform()] || impls.pwa`, so a present-but-empty ios function
        // would satisfy the lookup and defeat the pwa fallback — the component
        // would work on Android and in the browser and be silently dead on iOS.
        // That failure has shipped before; see the "Mounting" section of
        // native/BRIDGE_COMPONENTS.md. Omitting the key lets iOS fall through to
        // pwaPresent (false -> web drawer) until connect() supplies a live send.
        android: androidPresent,
        pwa: pwaPresent,
    });
}

// Facade may load after this module; register as soon as both are present.
if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "sheet";

    connect() {
        super.connect();
        if (window.ChippieBridge && window.ChippieBridge.register) {
            window.ChippieBridge.register("presentSheet", {
                ios: (url) => {
                    const path = appPath(url);
                    if (!path) return false;
                    this.send("present", { url: path });
                    return true;
                },
                android: androidPresent,
                pwa: pwaPresent,
            });
        }
    }

    disconnect() {
        registerStandalone();
        super.disconnect();
    }
}
